/**
 * Ladies Hub Subcategory Icon Mapping
 *
 * Shared config — single source of truth for all subcategory icons.
 * Used by LadiesHubSubcategorySheet and LadiesCategoryPicker.
 *
 * Icons come from two sources — both use identical visual specs
 * (24x24 viewBox, outline stroke, round caps):
 *   1. lucide-react  — for generic items
 *   2. ladiesCustomIcons — fashion/beauty-specific custom shapes
 */

import {
  Gem, Scissors, RefreshCcw,
  Zap, HeartPulse,
  Watch,
  Camera, Sparkles,
  Building2, GraduationCap,
  Palette, Gift, Cake, HandPlatter,
  Briefcase, PenTool,
  Tag,
  Baby, Package, Truck, Music, Mail, Home, Armchair,
  PaintBucket, Flower2,
} from 'lucide-react';

import {
  TraditionalDressIcon,
  DressIcon,
  GownIcon,
  HighHeelIcon,
  HandbagIcon,
  RingIcon,
  LipstickIcon,
  HairCombIcon,
  PerfumeIcon,
  HennaHandIcon,
  NecklaceIcon,
  NeedleThreadIcon,
  FabricIcon,
  EarringIcon,
  BraceletIcon,
  CandleIcon,
  SoapIcon,
  VaseIcon,
} from './ladiesCustomIcons';

const SUBCATEGORY_ICONS = {
  // ─── FASHION (8) ──────────────────────────────────
  traditional_wear:  TraditionalDressIcon,
  daily_casual:      DressIcon,
  occasion_wear:     GownIcon,
  footwear:          HighHeelIcon,
  bags_accessories:  HandbagIcon,
  kids_baby:         Baby,
  preloved:          RefreshCcw,
  wholesale_stock:   Package,

  // ─── BEAUTY (7) ────────────────────────────────────
  traditional_beauty: HennaHandIcon,
  skincare_makeup:   LipstickIcon,
  hair_care:         HairCombIcon,
  perfumes_fragrance: PerfumeIcon,
  salon_services:    Scissors,
  nail_care:         PaintBucket,
  beauty_tools:      Zap,

  // ─── JEWELLERY (8) ─────────────────────────────────
  gold_precious:     NecklaceIcon,
  traditional_jewelry: NecklaceIcon,
  rings:             RingIcon,
  necklaces_sets:    NecklaceIcon,
  earrings_bracelets: EarringIcon,
  watches:           Watch,
  handmade_artisan:  BraceletIcon,
  gemstones_custom:  Gem,

  // ─── EVENTS (8) ────────────────────────────────────
  venue_booking:     Building2,
  catering_food:     HandPlatter,
  bakery_sweets:     Cake,
  photography_media: Camera,
  decor_florals:     Flower2,
  sound_lighting:    Music,
  equipment_rental:  Package,
  invitations_printing: Mail,

  // ─── SERVICES (8) ──────────────────────────────────
  childcare:         Baby,
  delivery_logistics: Truck,
  educational_services: GraduationCap,
  business_admin:    Briefcase,
  digital_services:  PenTool,
  tailoring_alterations: NeedleThreadIcon,
  cleaning_home:     Sparkles,
  health_wellness:   HeartPulse,

  // ─── HANDMADE (8) ──────────────────────────────────
  home_decor:        Home,
  pottery_ceramics:  VaseIcon,
  textiles_embroidery: FabricIcon,
  leather_goods:     HandbagIcon,
  furniture_woodwork: Armchair,
  handmade_gifts:    Gift,
  candles_soaps:     CandleIcon,
  art_custom:        Palette,
};

/** Default fallback icon for unknown/new subcategories */
export const FALLBACK_ICON = Tag;

/**
 * Get the icon component for a subcategory ID.
 * Returns the fallback icon if no mapping exists.
 */
export function getSubcategoryIcon(subcategoryId) {
  return SUBCATEGORY_ICONS[subcategoryId] || FALLBACK_ICON;
}

export default SUBCATEGORY_ICONS;
